import { FetchFnArgs } from '../../types/apiTypes';
import { DeviceType } from '../../types/commonTypes';
import { DeviceStateAdapter } from './adapters';
import { useQueryFnDeps } from '../useQueryFnDeps';
import { useOperator } from '../../contexts/OperatorContext';
import { useQueries } from '@tanstack/react-query';
import { DeviceState } from './types';
import React, { useEffect, useState } from 'react';

const scope = 'device';

export const queryKeys = {
  all: [{ scope }] as const,
  state: (deviceType: DeviceType, deviceId: string) =>
    [{ scope, deviceType, deviceId, entity: 'state' }] as const,
};

type DeviceStateArgs = FetchFnArgs<{
  operatorType: OperatorType;
  operatorId: number;
  accountId: string;
  deviceType: DeviceType;
  requestDeviceType: DeviceType;
  deviceId: string;
}>;

type DeviceStatePayload = {
  url: string;
  init: RequestInit;
};

export async function getDeviceState(args: DeviceStateArgs) {
  const payload = deviceTypeUrlMap(args);

  const response = await fetch(payload.url, payload.init);

  return await DeviceStateAdapter(
    args.deviceType,
    args.requestDeviceType,
    response
  );
}

export const useDeviceState = (
  accountId: string,
  deviceId: string,
  deviceType: DeviceType
) => {
  const [pollTimeoutId, setPollTimeoutId] = useState<
    NodeJS.Timeout | undefined
  >();
  const [queriesEnabled, setQueriesEnabled] = useState(true);
  const [pollState, setPollState] = useState(false);
  const [offlineStates, setOfflineStates] = useState<DeviceState[]>([]);
  const [isRebooting, setIsRebooting] = useState(false);

  const deps = useQueryFnDeps();
  const { id: operatorId, type: operatorType } = useOperator();

  const deviceTypes = getDeviceStateQueries(deviceType);

  const queries = useQueries({
    queries: deviceTypes.map((requestDeviceType) => {
      const { signal } = new AbortController();

      return {
        queryKey: queryKeys.state(requestDeviceType, deviceId),
        queryFn: () =>
          getDeviceState({
            deviceType,
            requestDeviceType,
            deviceId,
            operatorType,
            operatorId,
            accountId,
            signal,
            ...deps,
          }),
        onSuccess: (d: DeviceState) => {
          if (!pollState) return;

          setOfflineStates((prev) => {
            let result = [...prev];

            const wasOffline = prev.some((s) => s.deviceType === d.deviceType);

            if (wasOffline && d.state === 'Online')
              return prev.filter((s) => s.deviceType !== d.deviceType);
            else if (!wasOffline && d.state === 'Offline') {
              result.push(d);
              return result;
            } else return prev;
          });
        },
        refetchOnWindowFocus: false,
        enabled:
          (queriesEnabled && !pollState) ||
          (queriesEnabled &&
            (offlineStates.length === 0 ||
              offlineStates.some((s) => s.deviceType === requestDeviceType))),
        staleTime: 0,
      };
    }),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);

  const data = React.useMemo(
    () =>
      queries.reduce((results: DeviceState[], query) => {
        if (query.data !== undefined) results.push(query.data);

        return results;
      }, []),
    [queries]
  );

  useEffect(() => {
    return () => clearTimeout(pollTimeoutId);
  }, [pollTimeoutId]);

  useEffect(() => {
    if (!isFetching && pollState) {
      const timeoutId = setTimeout(() => {
        setQueriesEnabled(true);
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [isFetching, queriesEnabled, pollState]);

  useEffect(() => {
    if (queriesEnabled && !pollState) {
      setQueriesEnabled(false);
    } else if (isFetching && pollState) {
      setQueriesEnabled(false);
    }
  }, [queriesEnabled, isFetching, pollState]);

  useEffect(() => {
    if (pollState && !isRebooting) {
      if (data.every((r) => r.state === 'Online')) setPollState(false);
    }
  }, [pollState, isRebooting, data]);

  const enablePolling = React.useCallback((timeout?: number) => {
    setPollState(true);
    setIsRebooting(true);

    setTimeout(() => {
      setIsRebooting(false);
    }, 1000 * 15);

    const timeoutId = setTimeout(() => {
      setQueriesEnabled(false);
      setPollState(false);
    }, timeout ?? 1000 * 60 * 5);

    setPollTimeoutId(timeoutId);
  }, []);

  const disablePolling = React.useCallback(() => {
    clearTimeout(pollTimeoutId);

    setOfflineStates([]);
    setPollState(false);
  }, [setPollState, pollTimeoutId]);

  return {
    enablePolling,
    disablePolling,
    isPolling: pollState,
    isLoading,
    isFetching,
    data,
    refetch: () =>
      queries.forEach((q) => {
        q.remove();
        q.refetch();
      }),
  };
};

const getDeviceStateQueries = (deviceType: DeviceType): DeviceType[] => {
  switch (deviceType) {
    case 'MediaRoom Play Set-Top Box':
      return ['MediaRoom Play Set-Top Box', 'MediaRoom Set-Top Box'];
    default:
      return [deviceType];
  }
};

const deviceTypeUrlMap = ({
  operatorId,
  accountId,
  requestDeviceType,
  deviceId,
  ...args
}: DeviceStateArgs): DeviceStatePayload => {
  let payload = {
    url: '',
    init: {
      method: 'GET',
      headers: {
        Authorization: args.access_token ?? '',
      },
      signal: args.signal,
    },
  };

  switch (requestDeviceType) {
    case 'Access Point': {
      payload.url = `${args.baseUrl}/${operatorId}/AccessPoints/State/${deviceId}`;

      break;
    }
    case 'ONT ZNID':
    case 'Cable Modem': {
      payload.url = `${args.baseUrl}/${operatorId}/SNMP/${deviceId}/Status`;
      payload.init.method = 'POST';

      break;
    }
    case 'MediaRoom Set-Top Box': {
      if (args.operatorType === 'Residential') {
        payload.url = `${args.baseUrl}/${operatorId}/Accounts/${accountId}/${deviceId}/State`;
      } else {
        payload.url = `${args.baseUrl}/${operatorId}/Accounts/${accountId}/MediaRoom/${deviceId}/State`;
      }

      break;
    }
    case 'MediaRoom Play Set-Top Box': {
      payload.url = `${args.baseUrl}/${operatorId}/Amino/${deviceId}/Status`;

      break;
    }
    default: {
      throw new Error(
        `Device type ${requestDeviceType} state is not supported`
      );
    }
  }

  return payload;
};
